import React from 'react'
import { RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * LedgerConsole3 (W1) — the hero artifact, dark. Tonight's price assembling
 * itself on a console: big ticking mono digits up top, glowing ledger lines
 * sliding in beneath, then a replay affordance.
 *
 * Evolved from marketing2's ConsoleLedger, but DE-SPEC'D per the landing3 plan
 * (§0 do-not-publish): the row data lives locally here (NOT imported from the v1
 * RateLedger, whose numbered presentation is retired) — there is no row
 * numbering, no counted-rule footer, and no how-often/read-back language. Every
 * visible string is quoted verbatim from the copy doc's §W1; the role="img" label
 * is the copy doc's §Widget aria-labels W1 line.
 *
 * All figures are illustrative marketing arithmetic (a room-rate example, not a
 * price card). Reduced motion (or no matchMedia): the fully-assembled console,
 * static — same pattern as ConsoleLedger.
 */

interface LedgerRow {
  /** Plain-English reason surface phrasing (no internal rule names) */
  reason: string
  /** Optional detail beneath the reason (e.g. the guardrails no-change note) */
  detail?: string
  /** Running total after this line — the canonical intermediate value */
  total: number
}

// Local row data, straight from the copy doc's §W1 — reason line · running total.
const LEDGER_ROWS: LedgerRow[] = [
  { reason: 'Base rate', total: 95 },
  { reason: 'Season +10%', total: 105 },
  { reason: 'Saturday +8%', total: 113 },
  { reason: 'Demand +6%', total: 120 },
  { reason: 'Guardrails', detail: 'within your bounds — no change', total: 120 },
]

// The final summary line — the big digits' reason caption + tonight's price.
const LEDGER_FINAL = { label: "Tonight's price", reason: 'Every pound accounted for', total: 120 }

const TOTAL_STEPS = LEDGER_ROWS.length + 1
const ROW_INTERVAL_MS = 620
const FIRST_ROW_DELAY_MS = 900

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/** rAF count-up used by the big digits. Static when animation is off. */
function useCountUp(target: number, animate: boolean, duration = 460) {
  const [display, setDisplay] = React.useState(animate ? 0 : target)
  const fromRef = React.useRef(animate ? 0 : target)

  React.useEffect(() => {
    if (!animate) {
      fromRef.current = target
      setDisplay(target)
      return
    }
    const from = fromRef.current
    if (from === target) return
    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      const value = Math.round(from + (target - from) * eased)
      setDisplay(value)
      if (p < 1) {
        raf = requestAnimationFrame(tick)
      } else {
        fromRef.current = target
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, animate, duration])

  return display
}

export default function LedgerConsole3({ className }: { className?: string }) {
  const [reduced] = React.useState(prefersReducedMotion)
  const [step, setStep] = React.useState(reduced ? TOTAL_STEPS : 0)
  const [runId, setRunId] = React.useState(0)

  React.useEffect(() => {
    if (reduced) {
      setStep(TOTAL_STEPS)
      return
    }
    setStep(0)
    const timers: ReturnType<typeof setTimeout>[] = []
    for (let i = 1; i <= TOTAL_STEPS; i++) {
      timers.push(setTimeout(() => setStep(i), FIRST_ROW_DELAY_MS + (i - 1) * ROW_INTERVAL_MS))
    }
    return () => timers.forEach(clearTimeout)
  }, [runId, reduced])

  const animate = !reduced
  const done = step >= TOTAL_STEPS
  // The big digits track the latest revealed line's running total.
  const currentTotal = step === 0 ? 0 : step > LEDGER_ROWS.length ? LEDGER_FINAL.total : LEDGER_ROWS[step - 1].total
  const digits = useCountUp(currentTotal, animate)

  return (
    <div
      className={cn('mkt3-panel mkt3-panel-hot overflow-hidden', className)}
      role="img"
      aria-label="A nightly rate being built line by line — a base rate rising through season, weekend and demand adjustments to a final price of £120, with every rule written down."
    >
      {/* Console header — caption is "Sat · 1 night" style, nothing else */}
      <div className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="mkt3-live-dot h-1.5 w-1.5 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(45,212,191,0.9)]"
          />
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-300">
            Rate Ledger
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Sat &middot; 1 night</span>
          {animate && (
            <button
              type="button"
              onClick={() => setRunId((r) => r + 1)}
              aria-label="Replay"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-slate-500 transition-all duration-300 hover:bg-white/10 hover:text-teal-300',
                done ? 'opacity-100' : 'pointer-events-none opacity-0'
              )}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-mono text-[10px] uppercase tracking-[0.16em]">Replay</span>
            </button>
          )}
        </div>
      </div>

      {/* The big digits — tonight's price assembling */}
      <div className="border-b border-white/[0.07] px-4 py-5 sm:px-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{LEDGER_FINAL.label}</p>
        <p
          className={cn(
            'mkt3-digits mkt3-digits-glow mt-1 text-[3.4rem] font-bold leading-none tabular-nums sm:text-[4rem]',
            done && animate && 'mkt3-final-pop'
          )}
        >
          £{digits}
          {!done && animate && (
            <span
              aria-hidden="true"
              className="mkt3-cursor ml-1 inline-block h-[0.75em] w-[3px] translate-y-[0.06em] bg-teal-300/80"
            />
          )}
        </p>
        <p
          className={cn(
            'mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-teal-300/90 transition-opacity duration-700',
            done ? 'opacity-100' : 'opacity-0'
          )}
        >
          {LEDGER_FINAL.reason}
        </p>
      </div>

      {/* Ledger lines — NO row numbering (de-spec'd) */}
      <ul className="divide-y divide-white/[0.05]">
        {LEDGER_ROWS.map((row, i) => {
          const active = step > i
          const latest = step === i + 1 && !done
          return (
            <li
              key={row.reason}
              className={cn(
                'mkt3-console-row flex items-baseline justify-between gap-3 px-4 py-2.5 sm:gap-4 sm:px-5',
                active && 'mkt3-on',
                latest && 'mkt3-latest'
              )}
            >
              <div className="min-w-0">
                <p className="text-[13px] font-medium leading-snug text-slate-200 sm:text-sm">{row.reason}</p>
                {row.detail && <p className="mt-0.5 text-xs leading-snug text-slate-500">{row.detail}</p>}
              </div>
              <span className="shrink-0 font-mono text-sm font-semibold tabular-nums text-slate-200">£{row.total}</span>
            </li>
          )
        })}
      </ul>

      {/* Closing line — replaces the retired counted-rule footer, no countable number */}
      <div className="border-t border-white/[0.07] px-4 py-3 sm:px-5">
        <p className="font-mono text-[10px] leading-relaxed tracking-[0.06em] text-slate-500">
          &hellip;and every other rule that touched tonight&rsquo;s price, written down.
        </p>
      </div>
    </div>
  )
}
